import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import { OmitStylesComponentProps } from '../../types';
import { cn } from '../../utils/cn';

export const avatarVariants = cva(
    'rocco-avatar relative flex shrink-0 rounded-full',
    {
        variants: {
            size: {
                square20: 'size-5',
                square24: 'size-6',
                square28: 'size-7',
                square32: 'size-8',
                square40: 'size-10',
                square48: 'size-12',
                square56: 'size-14',
                square64: 'size-16',
                square80: 'size-20',
                square112: 'size-28',
                square72: 'size-18',
                w60h90: 'h-90 w-60',
            },
            borderWidth: {
                none: 'border-none',
                small: 'border',
            },
            borderColor: {
                none: '',
                light: 'border-gray-200',
                dark: 'border-gray-900',
            },
            status: {
                active: 'active',
                inactive: 'inactive',
                none: '',
            },
        },
        defaultVariants: {
            size: 'square40',
            borderWidth: 'none',
            borderColor: 'none',
            status: 'none',
        },
    }
);

export const Avatar = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Root>,
    OmitStylesComponentProps<typeof AvatarPrimitive.Root> &
        VariantProps<typeof avatarVariants>
>(({ size, borderWidth, borderColor, status, className, ...props }, ref) => {
    // Determine dot color based on status
    const getDotColor = () => {
        switch (status) {
            case 'active':
                return '#2E8B11';
            case 'inactive':
                return '#FF0000';
            default:
                return null;
        }
    };

    const dotColor = getDotColor();

    return (
        <AvatarPrimitive.Root
            ref={ref}
            className={cn(
                avatarVariants({
                    size,
                    borderWidth,
                    borderColor,
                    status,
                    className,
                }),
                props.onClick ? 'cursor-pointer' : ''
            )}
            {...props}
        >
            {props.children}
            {dotColor && (
                <span
                    className="absolute bottom-1 right-1 z-10 h-4 w-4 rounded-full border-[1.5px] border-white"
                    style={{
                        backgroundColor: dotColor,
                    }}
                />
            )}
        </AvatarPrimitive.Root>
    );
});
Avatar.displayName = AvatarPrimitive.Root.displayName;
